import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CalendarBlank, CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const emptyRow = (date) => ({ date, available_from: "", available_to: "", unavailable: false, notes: "" });

export default function AvailabilityPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [expired, setExpired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [weeks, setWeeks] = useState(2);
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [hoursPerWeek, setHoursPerWeek] = useState(0);
  const [copySource, setCopySource] = useState(null);

  useEffect(() => {
    publicApi.get(`/portal/availability/${token}`)
      .then((r) => { setData(r.data); setHoursPerWeek(r.data.weekly_hours_committed || 0); })
      .catch((e) => {
        if (e.response?.status === 410) setExpired(true);
        else setInvalid(true);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const days = weeks * 7;
    const s = new Date(start);
    const existing = Object.fromEntries((data?.current_availability || []).map((a) => [a.date, a]));
    const list = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(s);
      d.setDate(s.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      const e = existing[iso];
      list.push(e
        ? { date: iso, available_from: e.available_from || "", available_to: e.available_to || "", unavailable: !!e.unavailable || (!e.available_from && !!e.unavailable_from), notes: e.notes || "" }
        : emptyRow(iso));
    }
    setRows(list);
  }, [weeks, start, data]);

  const copyFrom = (row) => {
    const { available_from, available_to, unavailable, notes } = row;
    setCopySource({ available_from, available_to, unavailable, notes });
    toast.success("Copied — click Paste on other days");
  };
  const pasteTo = (idx) => {
    if (!copySource) return;
    setRows((r) => r.map((row, i) => i === idx ? { ...row, ...copySource } : row));
  };
  const pasteWeekdays = () => {
    if (!copySource) return;
    setRows((r) => r.map((row) => {
      const dow = new Date(row.date).getDay();
      return (dow >= 1 && dow <= 5) ? { ...row, ...copySource } : row;
    }));
    toast.success("Applied to Mon-Fri");
  };

  const save = async () => {
    setSaving(true);
    try {
      // Normalize: if a row is marked unavailable, blank the available times
      const payload = rows.map((r) => r.unavailable
        ? { date: r.date, available_from: "", available_to: "", unavailable: true, notes: r.notes }
        : r);
      await publicApi.post(`/portal/availability/${token}`, { availability: payload, weekly_hours_committed: hoursPerWeek });
      toast.success("Availability updated — thanks");
    } catch (e) {
      if (e.response?.status === 410) { setExpired(true); return; }
      toast.error(e.response?.data?.detail || "Save failed");
    }
    finally { setSaving(false); }
  };

  if (loading) return <Center>Loading…</Center>;
  if (expired) return <ScreenMsg icon="warn" title="This link has expired" body="Availability links expire 24 hours after they are sent. Please contact SoverenX Systems to request a fresh link." />;
  if (invalid || !data) return <ScreenMsg icon="warn" title="Link not valid" body="This availability portal link is invalid or has been revoked. Please contact SoverenX Systems." />;

  const person = data.person;

  return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38]" data-testid="availability-portal">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <header className="mb-8 pb-6 border-b border-slate-200">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center">
              <CalendarBlank size={17} className="text-[#B7791F]" weight="bold" />
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.24em] text-slate-500">SoverenX · Availability Refresh</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Hi {person.name.split(" ")[0]} — update your availability</h1>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl leading-relaxed">
            Set your upcoming schedule so admin knows when you can be scheduled onto client engagements. Choose 1-4 weeks ahead, enter your available time per day, or tick <strong>Unavailable</strong> for days you can't work. Copy a typical day and paste to save time.
          </p>
          <p className="mt-2 text-[11px] font-mono text-amber-700">⏱ This link expires 24 hours after it was sent.</p>
          {data.availability_updated_at && (
            <div className="mt-2 text-[11px] font-mono text-slate-500">Last updated {data.availability_updated_at.slice(0, 16).replace("T", " ")}</div>
          )}
        </header>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-xs text-slate-600">How many weeks ahead?</Label>
              <select data-testid="portal-avail-weeks" value={weeks} onChange={(e) => setWeeks(parseInt(e.target.value))}
                className="mt-1 w-full h-10 px-3 rounded border border-slate-300 bg-white text-sm font-mono">
                <option value={1}>1 week</option><option value={2}>2 weeks</option><option value={3}>3 weeks</option><option value={4}>4 weeks</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Starting from</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Hours committed / week</Label>
              <Input data-testid="portal-hpw" type="number" min="0" max="60" value={hoursPerWeek}
                onChange={(e) => setHoursPerWeek(parseInt(e.target.value) || 0)} className="mt-1 font-mono" />
            </div>
          </div>

          {copySource && (
            <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded flex items-center justify-between gap-2 text-xs">
              <span className="font-mono text-emerald-800">Copied — click Paste on other days, or:</span>
              <Button size="sm" type="button" onClick={pasteWeekdays} variant="outline" className="h-7 text-[11px] border-emerald-300 text-emerald-700">Paste to Mon-Fri</Button>
            </div>
          )}

          <div className="border border-slate-200 rounded overflow-hidden max-h-[540px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-2 py-2">Date</th><th>Available From</th><th>To</th><th className="text-center">Unavailable</th><th>Notes</th><th className="pr-2 text-right">Copy</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const dow = new Date(row.date).toLocaleDateString(undefined, { weekday: "short" });
                  const isWeekend = [0, 6].includes(new Date(row.date).getDay());
                  const disabled = !!row.unavailable;
                  return (
                    <tr key={row.date} className={`border-b border-slate-100 ${isWeekend ? "bg-slate-50/50" : ""} ${disabled ? "opacity-60" : ""}`}>
                      <td className="px-2 py-2 font-mono"><div className="font-semibold">{row.date}</div><div className="text-[10px] text-slate-500 uppercase">{dow}</div></td>
                      <td><input type="time" value={row.available_from} disabled={disabled} onChange={(e) => setRows(rows.map((r, i) => i === idx ? { ...r, available_from: e.target.value } : r))} className="h-7 px-1 rounded border border-slate-300 text-xs font-mono w-24 disabled:bg-slate-100" /></td>
                      <td><input type="time" value={row.available_to} disabled={disabled} onChange={(e) => setRows(rows.map((r, i) => i === idx ? { ...r, available_to: e.target.value } : r))} className="h-7 px-1 rounded border border-slate-300 text-xs font-mono w-24 disabled:bg-slate-100" /></td>
                      <td className="text-center">
                        <label className="inline-flex items-center gap-1 cursor-pointer" title="Tick to mark this whole day as unavailable">
                          <input
                            data-testid={`portal-row-unavail-${idx}`}
                            type="checkbox"
                            checked={!!row.unavailable}
                            onChange={(e) => setRows(rows.map((r, i) => i === idx ? { ...r, unavailable: e.target.checked, available_from: e.target.checked ? "" : r.available_from, available_to: e.target.checked ? "" : r.available_to } : r))}
                            className="h-4 w-4 accent-rose-600"
                          />
                          <span className="text-[10px] font-mono text-rose-700 uppercase">Off</span>
                        </label>
                      </td>
                      <td><input type="text" value={row.notes} onChange={(e) => setRows(rows.map((r, i) => i === idx ? { ...r, notes: e.target.value } : r))} className="h-7 px-1 rounded border border-slate-300 text-xs w-32" placeholder="…" /></td>
                      <td className="pr-2 text-right">
                        <button type="button" onClick={() => copyFrom(row)} className="text-slate-500 hover:text-[#0F1E38] px-1">Copy</button>
                        <button type="button" onClick={() => pasteTo(idx)} disabled={!copySource} className="text-slate-500 hover:text-[#0F1E38] px-1 disabled:opacity-30">Paste</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-4">
            <Button data-testid="portal-avail-save" onClick={save} disabled={saving} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold h-11 px-6">
              {saving ? <><CircleNotch size={16} className="animate-spin mr-2" /> Saving…</> : "Save Availability"}
            </Button>
          </div>
        </div>

        <div className="text-center text-[10px] text-slate-400 font-mono uppercase tracking-wider">Private link · SoverenX Systems · Do not share</div>
      </div>
    </div>
  );
}

function Center({ children }) { return <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center text-xs font-mono uppercase tracking-wider text-slate-500">{children}</div>; }
function ScreenMsg({ icon, title, body }) {
  const Icon = icon === "ok" ? CheckCircle : Warning;
  const color = icon === "ok" ? "text-emerald-600" : "text-amber-600";
  const bg = icon === "ok" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200";
  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 shadow-sm text-center">
        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${bg} border`}><Icon size={22} className={color} weight="fill" /></div>
        <h2 className="mt-4 text-xl font-semibold text-[#0F1E38]">{title}</h2>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
